import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import {
  type InsightProductsSearch,
  insightProductsSearchSchema,
} from '~/features/catalog-enrichment/utils/insight-products';
import { navContextSearchSchema } from '~/features/catalog-enrichment/utils/nav-context';

const RunInsightProductsPage = lazy(
  () => import('~/features/catalog-enrichment/pages/run-insight-products-page'),
);

/** `{ kind, key, category?, group?, page? }` + the S8 `from`/`back` trail — every field falls back
 *  to a safe default, so a stale or hand-edited link still renders (an unknown row ⇒ a plain note). */
const searchSchema = insightProductsSearchSchema.extend(navContextSearchSchema);

/**
 * Insights detail view: every product behind one Insights row (an attribute's coverage, a failing
 * check or a performance signal). Not nested in the Insights route's component (`insights_`), but
 * under its URL, so the batch shell keeps the Insights tab highlighted.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-enrichment/runs/$runId/insights_/products',
)({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Group/page changes replace the history entry, so Back leaves the view rather than the page.
  const onSearchChange = (patch: Partial<InsightProductsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  return (
    <Suspense fallback={<PageSkeleton />}>
      {/* Keyed by row so a different row never carries this one's search box text. */}
      <RunInsightProductsPage
        key={`${runId}|${search.kind}|${search.key}|${search.category ?? ''}`}
        runId={runId}
        search={search}
        onSearchChange={onSearchChange}
      />
    </Suspense>
  );
}
